import React, { FC, useState, useEffect } from 'react'
import { IonInput, IonModal, IonContent, IonFooter, IonCheckbox } from '@ionic/react'
import { observer } from 'mobx-react'
import Config from 'common/config/config'
import { toast, modalLayer, getPlatform } from '@utils'
import { memberMobx } from '@mobxStore'
import { UseHeader, MktButton, UserNameInput, AddressMap, SaveCookieBar } from '@cps'
import { cloneDeep } from '@dt/fe-utils/dist/lodash'
import mktController, { IWxAddressType } from 'controllers/mktController'
import { wxConfigG } from 'controllers/third'
import MemberApi from '@api/member.api'
import LocationApi from '@api/location.api'
import i18n from 'i18next'
import TagModal from './tag'
import DeliveryController, { TCheckAddressStatus } from '@ctrls/deliveryController'
import { TEEatType } from 'restaurant/mobx/store.mobx'

import './index.scss'

const { isWeixin } = getPlatform()

interface IProps {
  info?: any;
  eatType?: TEatType;
  reject?: () => void;
  resolve?: (value: any) => void;
}

const AddressEditModal: FC<IProps> = (props: IProps) => {
  const defaultInfo: any = {
    lbsType: boJudge.checkKey('mapCn') || 'google',
    province: '',
    city: '',
    district: '',
    address: '',
    addressDetail: '',
    lng: -1,
    lat: -1,
    realName: '',
    gender: '',
    mobile: '',
    tag: '',
    isDefault: false
  }
  const {
    info,
    eatType,
    reject,
    resolve
  } = props
  const tagsArr: any[] = [i18n.t('location.addressTag1'), i18n.t('location.addressTag2'), i18n.t('location.addressTag3')]
  const [useWeiXin, setUseWeiXin] = useState<boolean>(false)
  const [addressInfo, setAddressInfo] = useState<any>(info || defaultInfo)
  const [tagBool, setTagBool] = useState<boolean>(false)
  const [editTag, setEditTag] = useState<string>('')
  const [saveCookie, setSaveCookie] = useState<boolean>(false)
  const [mobileInfo, setMobileInfo] = useState<any>({
    inputValue: '',
    valid: false
  })
  const changeInfo = (key: string, value: any) => {
    const resInfo: any = cloneDeep(addressInfo)
    resInfo[key] = value
    setAddressInfo(resInfo)
  }
  const getAddress = () => {
    modalLayer(AddressMap, {
      mapType: addressInfo.lbsType,
      center: addressInfo.lng !== -1 ? {
        lng: addressInfo.lng,
        lat: addressInfo.lat,
      } : null
    }).then(async (info: any) => {
      if (info.error) {
        toast({ message: info.message || i18n.t('location.notOpenLBSTip') })
      } else {
        if (info.mapType === 'baidu') {
          const resInfo: any = cloneDeep(addressInfo)
          resInfo.lbsType = info.mapType
          resInfo.province = info.province
          resInfo.city = info.city
          resInfo.district = info.address
          resInfo.address = info.title
          resInfo.lng = info.point.lng
          resInfo.lat = info.point.lat
          if (info.cityInfo) {
            resInfo.cityInfo = info.cityInfo
            const res: any = await LocationApi.getCnAreaCodeByName({
              cityName: info.cityInfo.city,
              provinceName: info.cityInfo.province,
              countyName: info.cityInfo.district
            })
            if (res.cityCode && res.provinceCode) {
              resInfo.extra = {
                cityCode: res.cityCode,
                provinceCode: res.provinceCode,
                countyCode: res.countyCode
              }
            }
          }
          setAddressInfo(resInfo)
        } else if (info.mapType === 'google') {
          const resInfo: any = cloneDeep(addressInfo)
          resInfo.lbsType = info.mapType
          resInfo.province = ''
          resInfo.city = ''
          resInfo.district = info.formatted_address
          resInfo.address = info.name
          resInfo.lng = info.geometry.location.lng()
          resInfo.lat = info.geometry.location.lat()
          if (info.cityInfo) {
            resInfo.cityInfo = {
              state: info.cityInfo?.state?.longName || '',
              county: info.cityInfo?.county?.longName || '',
              city: info.cityInfo?.city?.longName || '',
            }
            if (info.cityInfo.state.longName !== info.cityInfo.state.shortName) {
              resInfo.cityInfo.stateCode = info.cityInfo.state.shortName
            }
            const res: any = await MemberApi.getCityCode(resInfo.cityInfo)
            if (res.cityCode) {
              resInfo.cityId = res.cityCode
            }
          }
          setAddressInfo(resInfo)
        }
      }
    })
  }

  const handleSave = async () => {
    if (!addressInfo.realName) {
      toast({ message: i18n.t('location.enterReceiver') })
      return
    }
    if (!mobileInfo.inputValue || !mobileInfo.valid) {
      toast({ message: i18n.t('common.numberTips') })
      return
    }
    if (!addressInfo.address) {
      toast({ message: i18n.t('location.chooseYourAddress') })
      return
    }
    let checkStatus
    if (eatType === TEEatType.delivery) {
      checkStatus = await DeliveryController.checkAddressInArea(addressInfo)
    }
    if (checkStatus === TCheckAddressStatus.Cancel) return
    const para: any = {
      brandId: Config.BRANDID,
      lbsType: addressInfo.lbsType,
      province: addressInfo.province,
      city: addressInfo.city,
      district: addressInfo.district,
      address: addressInfo.address,
      addressDetail: addressInfo.addressDetail,
      lng: `${addressInfo.lng}`,
      lat: `${addressInfo.lat}`,
      realName: addressInfo.realName,
      gender: addressInfo.gender,
      mobile: mobileInfo.inputValue,
      countryCode: mobileInfo.countryCode,
      isDefault: addressInfo.isDefault,
      tag: addressInfo.tag,
    }
    if (Config.STOREID) para.storeId = Config.STOREID
    if (addressInfo.cityId) {
      para.cityId = addressInfo.cityId
    }
    if (addressInfo.extra) {
      para.extra = addressInfo.extra
    }
    if (memberMobx.isLogin) {
      if (info) {
        para.memberAddressId = info.memberAddressId
        memberMobx.updateMemberAddress(para).then(() => {
          resolve && resolve({ ...para, checkStatus })
        })
      } else {
        memberMobx.createMemberAddress(para).then(() => {
          resolve && resolve({ ...para, checkStatus })
        })
      }
    } else {
      if (saveCookie) {
        if (info) {
          const virRes = await memberMobx.virtualMember()
          if (virRes !== 0) {
            memberMobx.updateMemberAddress(para).then(() => {
              resolve && resolve({ ...para, checkStatus })
            })
          }
        } else {
          const virRes = await memberMobx.virtualMember()
          if (virRes !== 0) {
            memberMobx.createMemberAddress(para).then(() => {
              resolve && resolve({ ...para, checkStatus })
            })
          }
        }
      } else {
        resolve && resolve({ ...para, checkStatus })
      }
    }
  }
  const editTagHanlder = (tag: string) => {
    setEditTag(tag)
    setTagBool(true)
  }

  const getWxAddress = () => {
    mktController.openWxAddress().then((res: IWxAddressType) => {
      if (res) {
        const resInfo: any = cloneDeep(addressInfo)
        resInfo.lbsType = 'wechat'
        resInfo.province = res.provinceName
        resInfo.city = res.cityName
        resInfo.district = res.addressStreetFourthStageName
        resInfo.address = res.addressDetailInfoNew
        resInfo.lng = 0
        resInfo.lat = 0
        resInfo.realName = res.userName
        resInfo.mobile = res.telNumber
        resInfo.countryCode = 86
        setAddressInfo(resInfo)
        setMobileInfo({
          inputValue: resInfo.mobile,
          countryCode: resInfo.countryCode,
          valid: true
        })
      }
    })
  }

  useEffect(() => {
    if (info) {
      setAddressInfo(info)
      setMobileInfo({
        inputValue: info.mobile,
        countryCode: info.countryCode,
        valid: true
      })
    } else {
      setAddressInfo(defaultInfo)
      setMobileInfo({
        inputValue: '',
        valid: false
      })
    }
    if (isWeixin) {
      wxConfigG.init().then((res: boolean) => {
        setUseWeiXin(res)
      })
    }
  }, [info])
  return (
    <IonModal
      isOpen
      cssClass='address-edit-modal'
    >
      <UseHeader title={i18n.t('location.addShippingAddress')} onBack={reject}></UseHeader>
      <IonContent>
        <div className='modal-content'>
          <div className='input-area'>
            <div className='input-item'>
              <div className='input-top'>
                <div className='title'>{i18n.t('location.receiver')}</div>
                {/* {
                  useWeiXin &&
                    <div className='des' onClick={() => getWxAddress()}>
                      <span>{i18n.t('location.useWeChatAddress')}</span>
                      <i className='iconfont icon-weixin'></i>
                    </div>
                } */}
              </div>
              <div className='input-bottom'>
                <div className='name-input'>
                  <div className='input-area'>
                    <IonInput value={addressInfo.realName} placeholder={i18n.t('common.name')} onIonChange={(e: any) => changeInfo('realName', e.detail.value)} />
                  </div>
                  <div className='gender-area'>
                    <div className='gender-item' data-cypid='adressEditGender_1' onClick={() => changeInfo('gender', '1')}>
                      <IonCheckbox checked={addressInfo.gender === '1'}></IonCheckbox>
                      <span>{i18n.t('common.mr')}</span>
                    </div>
                    <div className='gender-item' data-cypid='adressEditGender_2' onClick={() => changeInfo('gender', '2')}>
                      <IonCheckbox checked={addressInfo.gender === '2'}></IonCheckbox>
                      <span>{i18n.t('common.mrs')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='input-item'>
              <div className='input-top'>
                <div className='title'>{i18n.t('mkt.common.mobile')}</div>
              </div>
              <div className='input-bottom'>
                <UserNameInput type='mobile' valueObj={mobileInfo} onChange={(val: any) => setMobileInfo(val)}></UserNameInput>
              </div>
            </div>
            <div className='input-item'>
              <div className='input-top'>
                <div className='title'>{i18n.t('common.address')}</div>
              </div>
              <div className='input-bottom'>
                <div className='address-info' data-cypid='adressEditGetAddressModal' onClick={() => getAddress()}>
                  {
                    addressInfo.lng !== -1
                      ? (
                        <div className='info-left'>
                          <div className='title'>{addressInfo.address}</div>
                          <div className='des'>{addressInfo.province}{addressInfo.city}{addressInfo.district}</div>
                        </div>
                      ) : <span className=''>{i18n.t('location.chooseYourAddress')}</span>
                  }
                  <i className='iconfont icon-togo0'></i>
                </div>
              </div>
            </div>
            <div className='input-item'>
              <div className='input-top'>
                <div className='title'>{i18n.t('location.houseNumber')}</div>
              </div>
              <div className='input-bottom'>
                <div className='mkt-input'>
                  <IonInput value={addressInfo.addressDetail} placeholder={i18n.t('location.addressTip')} data-cypid='adressEditInput' onIonChange={(e: any) => changeInfo('addressDetail', e.detail.value)} />
                </div>
              </div>
            </div>
            <div className='input-item'>
              <div className='input-top'>
                <div className='title'>{i18n.t('location.usedLabels')}</div>
              </div>
              <div className='input-bottom'>
                <div className='tag-area'>
                  {
                    tagsArr.map((item: any, index: number) => {
                      return (
                        <div
                          className={`tag-item ${item === addressInfo.tag ? 'selected' : ''}`} data-cypid={`adressEditChangeItem_${index}`} key={index} onClick={() => {
                            changeInfo('tag', item)
                          }}
                        >
                          <span>{item}</span>
                        </div>
                      )
                    })
                  }
                  {
                    addressInfo.tag && !tagsArr.includes(addressInfo.tag) ? (
                      <div className='tag-item selected' data-cypid='adressEditSelected' onClick={() => editTagHanlder(addressInfo.tag)}>
                        <span>{addressInfo.tag}</span>
                      </div>
                    ) : (
                      <div className='tag-item' data-cypid='adressEditUnselect' onClick={() => editTagHanlder('')}>
                        <i className='iconfont icon-add'></i>
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
            {/* <div className='default-area'>
              <span>{i18n.t('location.setDefaultAddress')}</span>
              <MktToggle checked={addressInfo.isDefault} onChange={(val: boolean) => changeInfo('isDefault', val)}></MktToggle>
            </div> */}
          </div>
        </div>
      </IonContent>
      <IonFooter>
        {
          !info && !memberMobx.isLogin &&
            <div style={{ paddingBottom: '18px' }}>
              <SaveCookieBar isSave={saveCookie} data-cypid='adressEditSaveCookie' onChange={(val: boolean) => setSaveCookie(val)}></SaveCookieBar>
            </div>
        }
        <MktButton text={i18n.t('mkt.common.confirm')} data-cypid='adressEditConfirm' onClick={handleSave}></MktButton>
      </IonFooter>
      {
        tagBool &&
          <TagModal
            tag={editTag} resolve={(res: any) => {
              setTagBool(false)
              if (res.type === 'delte') {
                changeInfo('tag', '')
              } else {
                changeInfo('tag', res.tag)
              }
            }}
          >
          </TagModal>
      }
    </IonModal>
  )
}

export default observer(AddressEditModal)
